import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { surveyAPI } from "@/lib/api";
import { Star } from "lucide-react";

const AdminSurveys = () => {

    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    /* ================= AUTH ================= */

    useEffect(() => {
        const token = localStorage.getItem("admin_token");
        if (!token) navigate("/admin/login");
        else fetchSurveys();
    }, [navigate]);

    /* ================= FETCH ================= */

    const fetchSurveys = async () => {
        try {
            const res = await surveyAPI.getAll();
            setSurveys(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /* ================= STATS ================= */

    const avgRating =
        surveys.length > 0
            ? (
                surveys.reduce((a, b) => a + b.rating, 0) /
                surveys.length
            ).toFixed(1)
            : 0;

    const recommendRate =
        surveys.length > 0
            ? Math.round(
                (surveys.filter(s => s.would_recommend).length /
                    surveys.length) * 100
            )
            : 0;

    /* ================= UI ================= */

    if (loading) {
        return (
            <div className="p-10 text-center">
                Yükleniyor...
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-light p-6">

            <div className="max-w-7xl mx-auto">

                <h1 className="text-2xl font-bold text-navy mb-8">
                    Memnuniyet Anketleri
                </h1>

                {/* ================= STATS ================= */}

                <div className="grid md:grid-cols-3 gap-6 mb-10">

                    <Stat
                        value={`${avgRating} ⭐`}
                        label="Ortalama Puan"
                    />

                    <Stat
                        value={surveys.length}
                        label="Toplam Anket"
                    />

                    <Stat
                        value={`%${recommendRate}`}
                        label="Tavsiye Oranı"
                    />

                </div>

                {/* ================= TABLE ================= */}

                <div className="bg-white rounded-lg shadow-sm overflow-x-auto">

                    <table className="w-full text-left">

                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="p-3">Puan</th>
                                <th>Hizmet</th>
                                <th>Personel</th>
                                <th>Zaman</th>
                                <th>Tavsiye</th>
                                <th>Yorum</th>
                                <th>Tarih</th>
                            </tr>
                        </thead>

                        <tbody>

                            {surveys.map((s) => (

                                <tr
                                    key={s.id}
                                    className="border-b hover:bg-gray-50"
                                >

                                    {/* PUAN */}
                                    <td className="p-3">
                                        <div className="flex">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Star
                                                    key={star}
                                                    size={16}
                                                    className={
                                                        star <= s.rating
                                                            ? "text-yellow-400 fill-yellow-400"
                                                            : "text-gray-300"
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </td>

                                    {/* SERVICE */}
                                    <td>{s.service_quality}</td>

                                    {/* STAFF */}
                                    <td>{s.staff_behavior}</td>

                                    {/* TIME */}
                                    <td>{s.on_time}</td>

                                    {/* RECOMMEND */}
                                    <td>
                                        {s.would_recommend ? (
                                            <span className="text-green-600 font-semibold">
                                                Evet 👍
                                            </span>
                                        ) : (
                                            <span className="text-red-600 font-semibold">
                                                Hayır 👎
                                            </span>
                                        )}
                                    </td>

                                    {/* COMMENT */}
                                    <td className="max-w-xs truncate">
                                        {s.comment || "-"}
                                    </td>

                                    {/* DATE */}
                                    <td>
                                        {new Date(
                                            s.created_at
                                        ).toLocaleDateString("tr-TR")}
                                    </td>

                                </tr>

                            ))}

                            {surveys.length === 0 && (
                                <tr>
                                    <td
                                        colSpan="7"
                                        className="p-6 text-center text-gray-500"
                                    >
                                        Henüz anket bulunmuyor
                                    </td>
                                </tr>
                            )}

                        </tbody>

                    </table>

                </div>

            </div>
        </div>
    );
};

/* ================= STAT CARD ================= */

const Stat = ({ value, label }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm text-center">
        <p className="text-3xl font-bold text-navy">
            {value}
        </p>
        <p className="text-slate-600">
            {label}
        </p>
    </div>
);

export default AdminSurveys;
